import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdvancedReportConfig } from '@app/advanced-reports/models/advanced-report-config';
import { TabExportData } from '@app/advanced-reports/dynamic-reporting/excel-export/advanced-reports-excel-export-orchestration.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { getIntQueryParam } from '@utils/route-utils';
import { pensionContributionsGridColumns } from './pension-contributions-grid-columns';
import { pensionPaymentErrorsGridColumns } from './pension-payment-errors-grid-columns';
import { pensionEnrollmentGridColumns } from './pension-enrollment-grid-columns';
import { PensionContributionsDataProvider } from './state/pension-contributions.data-provider';
import { TabConfig } from '@design/buttons/tab-list/tab-config';
import { combineLatest, firstValueFrom } from 'rxjs';
import { PensionContributionEmployee, PensionSubmissionError } from './state/pension-contribution-employee.model';
import { PensionEnrollmentRecord } from './state/pension-enrollment-record.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

type TabView = 'Enrollment data' | 'Contributions' | 'Payment errors' | 'Submission errors';

@UntilDestroy()
@Component({
  templateUrl: './pension-contributions-advanced-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PensionContributionsDataProvider],
  styleUrls: ['./pension-contributions-advanced-view.component.scss'],
  standalone: false
})
export class PensionContributionsAdvancedViewComponent {
  private readonly currentViewSubject = new BehaviorSubject<TabView>('Enrollment data');
  private readonly closedPeriodId: number | null;

  protected readonly enrollmentData$: Observable<PensionEnrollmentRecord[]>;
  protected readonly contributionsData$: Observable<PensionContributionEmployee[]>;
  protected readonly paymentErrorsData$: Observable<any[]>;
  protected readonly submissionErrorsData$: Observable<PensionSubmissionError[]>;
  readonly rowData$: Observable<PensionEnrollmentRecord[] | PensionContributionEmployee[] | PensionSubmissionError[] | any[]>;
  readonly config$: Observable<AdvancedReportConfig>;
  readonly tabs$: Observable<TabConfig[]>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dataProvider: PensionContributionsDataProvider
  ) {
    this.closedPeriodId = getIntQueryParam(this.route, 'closedPeriodId');

    this.enrollmentData$ = this.initializeEnrollmentData();
    this.contributionsData$ = this.initializeContributionsData();
    this.paymentErrorsData$ = this.initializePaymentErrorsData();
    this.submissionErrorsData$ = this.initializeSubmissionErrorsData();
    this.rowData$ = this.initializeRowData();
    this.config$ = this.initializeConfig();
    this.tabs$ = this.initializeTabs();
  }

  getTabExportData = async (tabLabel: string): Promise<Partial<TabExportData> | null> => {
    let rowData$: Observable<PensionEnrollmentRecord[] | PensionContributionEmployee[] | PensionSubmissionError[] | any[]>;
    let gridColumns: any[] = [];

    switch (tabLabel) {
      case 'Enrollment data':
        rowData$ = this.enrollmentData$;
        gridColumns = pensionEnrollmentGridColumns();
        break;
      case 'Contributions':
        rowData$ = this.contributionsData$;
        gridColumns = pensionContributionsGridColumns();
        break;
      case 'Payment errors':
        rowData$ = this.paymentErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      case 'Submission errors':
        rowData$ = this.submissionErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      default:
        rowData$ = this.enrollmentData$;
        gridColumns = pensionEnrollmentGridColumns();
    }

    const rowData = await firstValueFrom(rowData$);

    return {
      tabLabel,
      rowData,
      gridColumns
    };
  };

  private initializeEnrollmentData(): Observable<PensionEnrollmentRecord[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionEnrollments$(this.closedPeriodId).pipe(
      untilDestroyed(this),
      take(1),
      map((records) => this.sortEnrollmentRecords(records)),
      shareReplay(1)
    );
  }

  private initializeContributionsData(): Observable<PensionContributionEmployee[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionContributions$(this.closedPeriodId).pipe(
      untilDestroyed(this),
      take(1),
      map((contributions) => this.sortContributions(contributions)),
      shareReplay(1)
    );
  }

  private initializePaymentErrorsData(): Observable<any[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionPaymentErrors$(this.closedPeriodId).pipe(untilDestroyed(this), take(1), shareReplay(1));
  }

  private initializeSubmissionErrorsData(): Observable<PensionSubmissionError[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionSubmisssionsErrors$(this.closedPeriodId).pipe(untilDestroyed(this), take(1), shareReplay(1));
  }

  private initializeRowData(): Observable<PensionEnrollmentRecord[] | PensionContributionEmployee[] | PensionSubmissionError[] | any[]> {
    return this.currentViewSubject.pipe(
      untilDestroyed(this),
      switchMap((view) => {
        switch (view) {
          case 'Enrollment data':
            return this.enrollmentData$;
          case 'Contributions':
            return this.contributionsData$;
          case 'Payment errors':
            return this.paymentErrorsData$;
          case 'Submission errors':
            return this.submissionErrorsData$;
          default:
            return this.enrollmentData$;
        }
      })
    );
  }

  private initializeConfig(): Observable<AdvancedReportConfig> {
    return this.currentViewSubject.pipe(
      untilDestroyed(this),
      map((view) => ({
        gridId: `pension-${view.toLowerCase().replace(/\s+/g, '-')}-v4`,
        reportTitle: 'Pensions connect / Contributions',
        reportType: 'standard' as const,
        gridColumns$: of(this.getColumnsForView(view))
      }))
    );
  }

  private initializeTabs(): Observable<TabConfig[]> {
    return combineLatest([
      this.enrollmentData$,
      this.contributionsData$,
      this.paymentErrorsData$,
      this.submissionErrorsData$,
      this.currentViewSubject.asObservable()
    ]).pipe(
      untilDestroyed(this),
      map(([enrollments, contributions, paymentErrors, submissionErrors, currentView]) => {
        const tabs: TabConfig[] = [];

        if (enrollments.length > 0) {
          tabs.push({
            label: 'Enrollment data',
            tid: 'pension-enrollment-data-tab',
            active: currentView === 'Enrollment data',
            navigate: () => this.currentViewSubject.next('Enrollment data')
          });
        }

        if (contributions.length > 0) {
          tabs.push({
            label: 'Contributions',
            tid: 'pension-contributions-tab',
            active: currentView === 'Contributions',
            navigate: () => this.currentViewSubject.next('Contributions')
          });
        }

        if (submissionErrors.length > 0) {
          tabs.push({
            label: 'Submission errors',
            tid: 'pension-submission-errors-tab',
            active: currentView === 'Submission errors',
            navigate: () => this.currentViewSubject.next('Submission errors')
          });
        }

        if (paymentErrors.length > 0) {
          tabs.push({
            label: 'Payment errors',
            tid: 'pension-payment-errors-tab',
            active: currentView === 'Payment errors',
            navigate: () => this.currentViewSubject.next('Payment errors')
          });
        }

        return tabs;
      }),
      tap((tabs) => {
        const currentView = this.currentViewSubject.getValue();
        const hasActiveTab = tabs.some((tab) => tab.label === currentView);
        if (!hasActiveTab && tabs.length > 0) {
          this.currentViewSubject.next(tabs[0].label as TabView);
        }
      })
    );
  }

  private getColumnsForView(view: TabView): any[] {
    switch (view) {
      case 'Enrollment data':
        return pensionEnrollmentGridColumns();
      case 'Contributions':
        return pensionContributionsGridColumns();
      case 'Payment errors':
        return pensionPaymentErrorsGridColumns();
      case 'Submission errors':
        return pensionPaymentErrorsGridColumns();
      default:
        return pensionEnrollmentGridColumns();
    }
  }

  private sortContributions(contributions: PensionContributionEmployee[]): PensionContributionEmployee[] {
    return contributions.slice().sort((a, b) => {
      const aHasErrors = a.errors && a.errors.length > 0;
      const bHasErrors = b.errors && b.errors.length > 0;
      if (aHasErrors && !bHasErrors) return -1;
      if (!aHasErrors && bHasErrors) return 1;
      const surnameCompare = a.surname.localeCompare(b.surname);
      return surnameCompare !== 0 ? surnameCompare : a.firstName.localeCompare(b.firstName);
    });
  }

  private sortEnrollmentRecords(records: PensionEnrollmentRecord[]): PensionEnrollmentRecord[] {
    return records.slice().sort((a, b) => {
      if (a.hasErrors && !b.hasErrors) return -1;
      if (!a.hasErrors && b.hasErrors) return 1;
      const surnameCompare = (a.surname ?? '').localeCompare(b.surname ?? '');
      return surnameCompare !== 0 ? surnameCompare : (a.firstName ?? '').localeCompare(b.firstName ?? '');
    });
  }
}
